import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '../lib/dataSource'
import { onAppEvent } from '../lib/events'
import { useTestDataActions } from '../lib/testData'
import { TESTDATA_REQUEST_CHANNEL, type TestDataMenuRequest } from '../../shared/testDataMenu'
import { testDataCountTexts, useTestDataFlow, type TestDataOutcome } from '../components/common/TestData'
import { showSuccessToast } from '../components/common/SuccessToast'
import { dismissToast, showErrorToast, showInfoToast } from '../components/common/MessageToast'
import { loadTestData } from '../features/taskManagement'

/**
 * **Testdaten im Hauptfenster** (#269):
 * - Hilfe › „Testdaten anlegen / entfernen …“ (`testdata:request`) → Rückfrage, Ergebnis als Toast.
 * - `npm run testdata` (`dev:create-testdata`, nur Entwicklung) → legt ohne Rückfrage an, der Samen steht im
 *   Toast, damit sich ein Satz nachstellen lässt.
 *
 * Gibt die Props der Rückfrage zurück; MainLayout rendert `<TestDataConfirmDialog {...dialog} />`.
 */
export function useTestDataMenuBridge() {
  const { t } = useTranslation()
  const flow = useTestDataFlow(useTestDataActions(), { onOutcome: outcome => showOutcomeToast(outcome, t) })

  const askRef = useRef(flow.ask)
  askRef.current = flow.ask
  useEffect(() => {
    if (!window.api?.receive) return
    return window.api.receive(TESTDATA_REQUEST_CHANNEL, (request?: TestDataMenuRequest) => {
      if (request?.action === 'create' || request?.action === 'remove') void askRef.current(request.action)
    })
  }, [])

  // Dasselbe aus dem ? über dem Profilbild (#323) — auch im Web, wo es kein App-Menü gibt.
  useEffect(() => onAppEvent('testdata-request', ({ action }) => { void askRef.current(action) }), [])

  // Entfernen dauert ein paar Sekunden (je Aufgabe ein Schreibvorgang) — so lange ein Hinweis, auch über die
  // fünf Sekunden des Toasters hinaus.
  useEffect(() => {
    if (!flow.busy) return
    const id = showInfoToast(t(flow.busy === 'create' ? 'testData.creating' : 'testData.removing'), { duration: Infinity })
    return () => dismissToast(id)
  }, [flow.busy, t])

  const source = useDataSource()
  useEffect(() => {
    if (!window.api?.receive) return
    return window.api.receive('dev:create-testdata', (request?: { seed?: number }) => {
      void loadTestData()
        .then(({ createTestData }) => createTestData(source, { seed: request?.seed }))
        .then(created => showOutcomeToast({ kind: 'created', created }, t))
        .catch((error: unknown) => {
          console.error('[testdata]', error)
          showOutcomeToast({ kind: 'failed', action: 'create' }, t)
        })
    })
  }, [source, t])

  return flow.dialog
}

function showOutcomeToast(outcome: TestDataOutcome, t: ReturnType<typeof useTranslation>['t']) {
  switch (outcome.kind) {
    case 'created':
      showSuccessToast({
        message: t('testData.created'),
        description: t('testData.createdText', { ...testDataCountTexts(t, outcome.created), seed: outcome.created.seed }),
      })
      return
    case 'removed':
      showSuccessToast({ message: t('testData.removed'), description: t('testData.removedText', testDataCountTexts(t, outcome.removed)) })
      return
    case 'none':
      showInfoToast(t('testData.none'))
      return
    case 'failed':
      showErrorToast(t(outcome.action === 'create' ? 'testData.createFailed' : 'testData.removeFailed'))
  }
}
